import { useEffect } from 'react'
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
}

interface ToastProps {
  toast: Toast
  onClose: (id: string) => void
}

export function ToastItem({ toast, onClose }: ToastProps) {
  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => {
        onClose(toast.id)
      }, toast.duration || 5000)

      return () => clearTimeout(timer)
    }
  }, [toast.id, toast.duration, onClose])

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle2 size={20} color="var(--color-lime-green)" />
      case 'error':
        return <XCircle size={20} color="var(--color-coral-red)" />
      case 'warning':
        return <AlertCircle size={20} color="var(--color-amber-500)" />
      case 'info':
        return <Info size={20} color="var(--color-sky-blue)" />
      default:
        return <Info size={20} color="var(--color-sky-blue)" />
    }
  }

  const getBackgroundColor = () => {
    switch (toast.type) {
      case 'success':
        return 'rgba(152, 202, 63, 0.1)'
      case 'error':
        return 'rgba(255, 76, 91, 0.1)'
      case 'warning':
        return 'rgba(255, 193, 7, 0.1)'
      case 'info':
        return 'rgba(0, 174, 239, 0.1)'
      default:
        return 'rgba(0, 174, 239, 0.1)'
    }
  }

  const getBorderColor = () => {
    switch (toast.type) {
      case 'success':
        return 'var(--color-lime-green)'
      case 'error':
        return 'var(--color-coral-red)'
      case 'warning':
        return 'var(--color-amber-500)'
      case 'info':
        return 'var(--color-sky-blue)'
      default:
        return 'var(--color-sky-blue)'
    }
  }

  const getTextColor = () => {
    switch (toast.type) {
      case 'success':
        return 'var(--color-lime-green)'
      case 'error':
        return 'var(--color-coral-red)'
      case 'warning':
        return 'var(--color-amber-500)'
      case 'info':
        return 'var(--color-sky-blue)'
      default:
        return 'var(--color-gray-900)'
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem 1.25rem',
        background: getBackgroundColor(),
        border: `1px solid ${getBorderColor()}`,
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        minWidth: '300px',
        maxWidth: '500px',
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      <div style={{ flexShrink: 0, marginTop: '0.125rem' }}>
        {getIcon()}
      </div>
      <p
        style={{
          margin: 0,
          flex: 1,
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: getTextColor(),
          lineHeight: 'var(--leading-relaxed)',
        }}
      >
        {toast.message}
      </p>
      <button
        onClick={() => onClose(toast.id)}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.25rem',
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: getTextColor(),
          opacity: 0.7,
          transition: 'all var(--default-transition-duration) var(--ease-in-out)',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1'
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7'
          e.currentTarget.style.background = 'transparent'
        }}
      >
        <X size={16} />
      </button>
    </div>
  )
}

interface ToastContainerProps {
  toasts: Toast[]
  onClose: (id: string) => void
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} style={{ pointerEvents: 'auto' }}>
          <ToastItem toast={toast} onClose={onClose} />
        </div>
      ))}
    </div>
  )
}

